import { finalize, filter, take } from 'rxjs/operators';
import some from 'lodash-es/some';
import { combineLatest } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { Severity } from '../flash-messages/flash-message.model';
import { FlashMessagesService } from '../flash-messages/flash-messages.service';
import { GpsLogDateGroup } from '../gps-logs/gps-log-date-group.model';
import { GpsLogDeviceGroup } from '../gps-logs/gps-log-device-group.model';
import { GpsLogsService, RequestType } from '../gps-logs/gps-logs.service';
import { SitesService } from '../sites/sites.service';
import { GpsLogItem } from '../gps-logs/gps-log-item.model';
var SearchService = /** @class */ (function () {
    function SearchService(authService, sitesService, flashMessagesService, gpsLogsService) {
        var _this = this;
        this.authService = authService;
        this.flashMessagesService = flashMessagesService;
        this.gpsLogsService = gpsLogsService;
        // See: https://stackoverflow.com/questions/15965166/what-is-the-maximum-length-of-latitude-and-longitude
        this.maxLatitude = 90;
        this.maxLongitude = 180;
        this.messageIds = {
            httpError: null,
            noResults: null
        };
        this.minLatitude = -90;
        this.minLongitude = -180;
        this.searching = false;
        this.site = null;
        this.sites = [];
        this.siteSelectEnabled = false;
        this.userInfo = null;
        this._latitude = null;
        this._longitude = null;
        // Wait for both sites and the first valid user info emission
        combineLatest([
            sitesService.all(),
            // Use filter and take(1) to get the first non-null user info
            authService.userInfoSubject.pipe(filter(function (user) { return !!user; }), take(1))
        ]).subscribe(function (_a) {
            var sites = _a[0], userInfo = _a[1];
            _this.sites = sites;
            _this.userInfo = userInfo; // this.userInfo is set here
            // Now safely perform role-based logic
            if (_this.userInfo.isInRole('council')) {
                _this.site = _this.sites.find(function (f) { return f.siteNumber === _this.userInfo.councilSiteNumber; });
                _this.siteSelectEnabled = false; // Ensure flag is set correctly
            }
            else {
                _this.siteSelectEnabled = true;
                // Site remains null until selected by the user
                _this.site = null;
            }
        }, function (error) {
            console.error("SearchService: Error fetching sites or initial user info:", error);
            _this.flashMessagesService.addMessage(Severity.Danger, "Failed to load site or user data for search.");
        });
    }
    Object.defineProperty(SearchService.prototype, "latitude", {
        get: function () {
            return this._latitude;
        },
        set: function (value) {
            this._latitude = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(SearchService.prototype, "longitude", {
        get: function () {
            return this._longitude;
        },
        set: function (value) {
            this._longitude = value;
        },
        enumerable: true,
        configurable: true
    });
    SearchService.prototype.clearAll = function () {
        this.latitude = null;
        this.longitude = null;
        if (this.siteSelectEnabled) {
            this.site = null;
        }
        this.clearResults();
        this.clearWarnings();
    };
    SearchService.prototype.clearResults = function () {
        this.gpsLogDateGroups = null;
        this.gpsLogItems = null;
        this.gpsLogsResponse = null;
    };
    SearchService.prototype.clearWarnings = function () {
        this.flashMessagesService.clear(this.messageIds.httpError);
        this.flashMessagesService.clear(this.messageIds.noResults);
    };
    SearchService.prototype.isValid = function () {
        var _this = this;
        return !!(this.latitude
            && this.latitude >= this.minLatitude
            && this.latitude <= this.maxLatitude
            && this.longitude
            && this.longitude >= this.minLongitude
            && this.longitude <= this.maxLongitude
            && this.site
            && this.site.siteNumber
            && some(this.sites, function (site) { return (site && site.siteNumber) && (_this.site.siteNumber === site.siteNumber); }));
    };
    SearchService.prototype.search = function (address, latitude, longitude) {
        var _this = this;
        if (address !== null) {
            this.address = address;
        }
        else {
            this.latitude = latitude;
            this.longitude = longitude;
        }
        // Relying on GpsSearchComponent to wait for auth before calling search.
        // The constructor should have set this.userInfo by now.
        if (!this.searching && this.isValid()) {
            this.searching = true;
            this.clearResults();
            this.clearWarnings();
            // If this line fails, it means the component calling search didn't wait properly.
            var requestType = this.userInfo.isInRole('jjr') ? RequestType.Site : RequestType.Council;
            this.gpsLogsService.get(requestType, this.siteSelectEnabled ? this.site.siteNumber : '', this.latitude, this.longitude).pipe(finalize(function () {
                if (address === null) {
                    _this.latitude = latitude;
                    _this.longitude = longitude;
                }
                _this.searching = false;
            })).subscribe(function (gpsLogsResponse) {
                if (gpsLogsResponse.filteredResultsCount === 0) {
                    _this.messageIds.noResults = _this.flashMessagesService.addMessage(Severity.Warning, 'No search results found.');
                }
                else {
                    _this.gpsLogsResponse = gpsLogsResponse;
                    // Use a different loop variable name to avoid shadowing
                    _this.gpsLogsResponse.gpsLogs.forEach(function (logEntry) { return _this.constructResult(logEntry); });
                }
            }, function (error) {
                _this.messageIds.httpError = _this.flashMessagesService.addMessageFor(error);
            });
        }
    };
    SearchService.prototype.selectSite = function (site) {
        if (this.siteSelectEnabled) {
            this.site = site;
            this.clearWarnings();
            this.clearResults();
        }
    };
    // noinspection JSMethodCanBeStatic
    SearchService.prototype.compareDateOnly = function (d1, d2) {
        return new Date(d1).setHours(0, 0, 0, 0) === new Date(d2).setHours(0, 0, 0, 0);
    };
    SearchService.prototype.constructResult = function (log) {
        var _this = this;
        var item = new GpsLogItem(log);
        if (this.gpsLogItems == null) {
            this.gpsLogItems = [];
        }
        this.gpsLogItems.push(item);
        var dateGroup;
        if (this.gpsLogDateGroups == null) {
            this.gpsLogDateGroups = [];
        }
        else {
            var date_1 = item.timestamp.toDate();
            dateGroup = this.gpsLogDateGroups.find(function (r) { return _this.compareDateOnly(r.date, date_1); });
        }
        if (dateGroup == null) {
            dateGroup = new GpsLogDateGroup(item);
            this.gpsLogDateGroups.push(dateGroup);
        }
        else {
            var deviceGroup = void 0;
            deviceGroup = dateGroup.devices.find(function (r) { return r.deviceId === log.deviceId; });
            if (deviceGroup == null) {
                dateGroup.devices.push(new GpsLogDeviceGroup(item));
            }
            else {
                deviceGroup.items.push(item);
            }
        }
    };
    return SearchService;
}());
export { SearchService };
